import { Button, Colors, MuiModal, Text } from '@pinchenterprisesnpm/friday-ui'
import { RichTextEditor } from '@src/components/business/RichTextEditor'
import { InfoBanner } from '@src/features/shift-post-v2/components/info-banner/info-banner'
import { CustomAsyncSelect } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/custom-async-select'
import { FormatOptionLabel } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/format-option-label/format-option-label'
import { useEditArrivalInstructions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/hooks/useEditArrivalInstructions'
import {
  ILocationSectionFormValues,
  TLocationDropdownOption,
} from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.types'
import { debouncedLoadLocationOptions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.utils'
import { connect, FormikProps } from 'formik'
import { useMemo, useState } from 'react'

const { Header, Content, Footer } = MuiModal

interface IEditArrivalInstructionsModalProps {
  isOpen: boolean
  closeModal: () => void
  isConfirmStep?: boolean
  formik?: FormikProps<ILocationSectionFormValues>
  handleLocationChange: (
    location: TLocationDropdownOption,
    customArrivalInstructions?: string,
  ) => void
}

export const EditArrivalInstructionsModal = connect<
  IEditArrivalInstructionsModalProps
>(({ isOpen, closeModal, formik, isConfirmStep, handleLocationChange }) => {
  const {
    values: { location, property_id, keyForLocationsDropdown },
    errors,
  } = formik

  const [modalSelectedLocation, setModalSelectedLocation] = useState<
    TLocationDropdownOption
  >(location)

  const {
    arrivalInstructions,
    arrivalInstructionsErrors,
    setArrivalInstructions,
    handleEditArrivalInstructions,
    isSubmitting,
  } = useEditArrivalInstructions(
    formik,
    closeModal,
    modalSelectedLocation,
    handleLocationChange,
    isConfirmStep,
    location,
    property_id,
  )

  const handleDropdownLocationChange = (location: TLocationDropdownOption) => {
    setModalSelectedLocation(location)
    setArrivalInstructions(location?.property_arrival_instructions)
  }

  const handleCancel = () => {
    closeModal()
    setArrivalInstructions(location?.property_arrival_instructions)
  }

  // TODO(RMAP-362): FIX in the UI refinement Ticket
  const description = `Editing Arrival Instructions here will edit them for all shifts posted at ${location?.name}.\nAre you sure you want to edit?`

  const title = useMemo(() => {
    return isConfirmStep ? 'Edit Location' : 'Edit Arrival Instructions'
  }, [isConfirmStep])

  return (
    <MuiModal
      isOpen={isOpen}
      closeModal={handleCancel}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          height: '550px',
        },
      }}
    >
      <Header title={title} />

      <Content>
        {isConfirmStep && (
          <>
            <Text my="0" color={Colors.dark}>
              You can edit location and the arival instructions from here
            </Text>

            <CustomAsyncSelect
              label="Location"
              menuPlacement="bottom"
              value={modalSelectedLocation}
              placeholder="Please select a location..."
              isOutlined
              rounded={true}
              size="large"
              loadOptions={debouncedLoadLocationOptions}
              defaultOptions
              my="sm"
              onChange={handleDropdownLocationChange}
              key={keyForLocationsDropdown}
              errorMessage={
                errors.property_id && !property_id
                  ? errors.property_id
                  : undefined
              }
              formatOptionLabel={(value: TLocationDropdownOption) => (
                <FormatOptionLabel value={value} />
              )}
              ptValueContainer="16px"
            />
          </>
        )}

        {!isConfirmStep && <InfoBanner description={description} />}

        <Text my="sm">Arrival Instructions</Text>

        <RichTextEditor
          placeholder="Arrival Instructions *"
          name="property_arrival_instructions"
          value={arrivalInstructions}
          handleChange={(_, value) => setArrivalInstructions(value)}
          errorMessage={arrivalInstructionsErrors}
          fieldTouched={true}
          borderRadius="10px"
          minHeight="200px"
          borderColor={Colors.border}
        />
      </Content>

      <Footer>
        <Button isFilledOutlined onClick={handleCancel}>
          Cancel
        </Button>

        <Button
          onClick={handleEditArrivalInstructions}
          isDisabled={isSubmitting || !!arrivalInstructionsErrors}
          isLoading={isSubmitting}
        >
          Save
        </Button>
      </Footer>
    </MuiModal>
  )
})
